import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  SparklesIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  ShoppingBagIcon,
  BookOpenIcon,
  PlayIcon,
  UsersIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline'

const HomePage: React.FC = () => {
  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl"
      >
        <div className="card-glass p-8 md:p-12 lg:p-16">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-500/20 text-primary-300 border border-primary-500/30">
                <SparklesIcon className="h-4 w-4 mr-2" />
                Welcome to ConnectHub
              </span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-6"
            >
              Where Stories
              <span className="gradient-text block">Connect Hearts</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl md:text-2xl text-white/70 mb-8 max-w-2xl"
            >
              Share your journey, discover meaningful connections, and explore a world of premium experiences in our sophisticated community.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/stories" className="btn-primary text-lg px-8 py-4">
                <BookOpenIcon className="h-5 w-5 mr-2" />
                Start Your Story
              </Link>
              <Link to="/dating" className="btn-glass text-lg px-8 py-4">
                <HeartIcon className="h-5 w-5 mr-2" />
                Find Connections
              </Link>
            </motion.div>
          </div>
          
          {/* Floating Elements */}
          <div className="absolute top-20 right-20 float-animation">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-primary-500/20 to-secondary-500/20 backdrop-blur-md"></div>
          </div>
          <div className="absolute bottom-20 right-40 float-animation" style={{ animationDelay: '2s' }}>
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-accent-500/20 to-primary-500/20 backdrop-blur-md"></div>
          </div>
        </div>
      </motion.section>

      {/* Stats Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-6"
      >
        <StatsCard
          icon={UsersIcon}
          number="10M+"
          label="Active Users"
          color="primary"
        />
        <StatsCard
          icon={HeartIcon}
          number="2.5M"
          label="Matches Made"
          color="secondary"
        />
        <StatsCard
          icon={BookOpenIcon}
          number="500K+"
          label="Stories Shared"
          color="accent"
        />
        <StatsCard
          icon={GlobeAltIcon}
          number="150+"
          label="Countries"
          color="primary"
        />
      </motion.section>

      {/* Features Grid */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        <FeatureCard
          icon={BookOpenIcon}
          title="Story Timeline"
          description="Share your relationship journey through captivating chapter-based stories with rich media content."
          link="/stories"
          color="primary"
        />
        <FeatureCard
          icon={HeartIcon}
          title="Smart Dating"
          description="Advanced matching algorithms with location-based discovery and compatibility scoring."
          link="/dating"
          color="secondary"
        />
        <FeatureCard
          icon={ShoppingBagIcon}
          title="Premium Shop"
          description="Curated marketplace for adult products with discreet delivery and secure payments."
          link="/shop"
          color="accent"
        />
        <FeatureCard
          icon={ChatBubbleLeftIcon}
          title="Real-time Chat"
          description="Instant messaging with end-to-end encryption, video calls, and voice messages."
          link="/chat"
          color="primary"
        />
        <FeatureCard
          icon={PlayIcon}
          title="Live Streaming"
          description="Broadcast live content, connect with viewers, and monetize your creativity."
          link="/live"
          color="secondary"
        />
        <FeatureCard
          icon={SparklesIcon}
          title="Premium Content"
          description="Exclusive access to geo-restricted content with multiple subscription tiers."
          link="/premium"
          color="accent"
        />
      </motion.section>

      {/* Trending Stories Preview */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="space-y-6"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-display font-bold">Trending Stories</h2>
          <Link to="/stories" className="btn-glass">
            View All Stories
          </Link>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StoryPreviewCard
            title="From Friends to Forever"
            author="Sarah & Mike"
            chapters={12}
            likes={1420}
          />
          <StoryPreviewCard
            title="Digital Love in Tokyo"
            author="Akira"
            chapters={8}
            likes={892}
          />
          <StoryPreviewCard
            title="Second Chances"
            author="Maria"
            chapters={15}
            likes={2156}
          />
        </div>
      </motion.section>
    </div>
  )
}

interface StatsCardProps {
  icon: React.ComponentType<any>
  number: string
  label: string
  color: 'primary' | 'secondary' | 'accent'
}

const StatsCard: React.FC<StatsCardProps> = ({ icon: Icon, number, label, color }) => {
  const colorClasses = {
    primary: 'from-primary-600/20 to-primary-400/20 text-primary-300',
    secondary: 'from-secondary-600/20 to-secondary-400/20 text-secondary-300',
    accent: 'from-accent-600/20 to-accent-400/20 text-accent-300'
  }

  return (
    <motion.div
      whileHover={{ scale: 1.05, y: -5 }}
      className={`card-glass p-6 bg-gradient-to-br ${colorClasses[color]}`}
    >
      <Icon className="h-8 w-8 mb-4" />
      <p className="text-2xl font-bold text-white">{number}</p>
      <p className="text-sm text-white/70">{label}</p>
    </motion.div>
  )
}

interface FeatureCardProps {
  icon: React.ComponentType<any>
  title: string
  description: string
  link: string
  color: 'primary' | 'secondary' | 'accent'
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon: Icon, title, description, link, color }) => {
  const colorClasses = {
    primary: 'hover:border-primary-500/30 hover:shadow-primary-500/20',
    secondary: 'hover:border-secondary-500/30 hover:shadow-secondary-500/20',
    accent: 'hover:border-accent-500/30 hover:shadow-accent-500/20'
  }

  return (
    <Link to={link}>
      <motion.div
        whileHover={{ scale: 1.03, y: -8 }}
        className={`story-card h-full ${colorClasses[color]}`}
      >
        <Icon className="h-12 w-12 text-primary-400 mb-4" />
        <h3 className="text-xl font-bold mb-3">{title}</h3>
        <p className="text-white/70 leading-relaxed">{description}</p>
      </motion.div>
    </Link>
  )
}

interface StoryPreviewCardProps {
  title: string
  author: string
  chapters: number
  likes: number
}

const StoryPreviewCard: React.FC<StoryPreviewCardProps> = ({ title, author, chapters, likes }) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -5 }}
    className="story-card cursor-pointer"
  >
    <div className="aspect-w-16 aspect-h-9 mb-4 rounded-xl overflow-hidden bg-gradient-to-br from-primary-500/20 to-secondary-500/20">
      <div className="w-full h-40 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
        <PlayIcon className="h-12 w-12 text-white/50" />
      </div>
    </div>
    <h3 className="font-bold text-lg mb-2">{title}</h3>
    <p className="text-white/70 text-sm mb-3">by {author}</p>
    <div className="flex items-center justify-between text-sm text-white/50">
      <span>{chapters} chapters</span>
      <div className="flex items-center">
        <HeartIcon className="h-4 w-4 mr-1" />
        <span>{likes}</span>
      </div>
    </div>
  </motion.div>
)

export default HomePage